import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { find, isEmpty, reject, some } from "lodash/fp";

import apiClient from "@app/common/api/apiClient";
import useAuth from "@app/common/auth/useAuth";
import {
  ApiEntityAccessPatchBody,
  EventGroupACLDeleteRequest,
  EventGroupACLPatchRequest,
  EventGroupACLResponse,
} from "@app/common/types";
import { replaceWhere } from "@app/utils/arrays";

const useUpcomingEventAccess = (
  id: string,
  opts = { enabled: true, refetchOnMount: false }
) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const queryKey = ["eventGroupACL", user?.id, id];

  const {
    data: accessList,
    isError,
    isLoading,
  } = useQuery({
    queryKey,
    queryFn: () => apiClient.eventGroups.getEventGroupACL(id),
    enabled: opts.enabled && Boolean(id),
    refetchOnMount: opts.refetchOnMount,
  });

  const grantAccessMutation = useMutation({
    mutationFn: ({
      entityAccess,
      applyToRecurringEvent,
    }: {
      entityAccess: EventGroupACLPatchRequest["entityAccess"];
      applyToRecurringEvent: boolean;
    }) => {
      if (isEmpty(entityAccess)) return Promise.resolve();
      return apiClient.eventGroups.patchEventGroupACL(id, {
        entityAccess,
        applyToRecurringEvent,
      });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const modifyAccessMutation = useMutation({
    mutationFn: (body: ApiEntityAccessPatchBody) =>
      apiClient.eventGroups.patchEventGroupACL(id, {
        entityAccess: [body],
      }),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, (previous: EventGroupACLResponse) => {
        const previousResponse = previous;
        const existingEntityAccess = find(
          { entityId: body.entityId },
          previousResponse.entityAccess
        );

        if (!existingEntityAccess) return previousResponse;

        const { notifyUser: _n, message: _m, ...patchBody } = body;
        const mutatedEntityAccess = {
          ...existingEntityAccess,
          ...patchBody,
        };

        const mutatedResponse: EventGroupACLResponse = {
          ...previousResponse,
          entityAccess: replaceWhere(
            previousResponse.entityAccess,
            mutatedEntityAccess,
            (e) => e.entityId === body.entityId
          ),
        };
        return mutatedResponse;
      });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const revokeAccessMutation = useMutation({
    mutationFn: (body: EventGroupACLDeleteRequest) =>
      apiClient.eventGroups.deleteEventGroupACL(id, body),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, (previous) => {
        const previousResponse = previous as EventGroupACLResponse;
        const mutatedResponse: EventGroupACLResponse = {
          ...previousResponse,
          entityAccess: reject(
            { entityId: body.entityId },
            previousResponse.entityAccess
          ),
        };
        return mutatedResponse;
      });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  const bulkAccessMutation = useMutation({
    mutationFn: async ({
      modifyAccessOperations,
      revokeAccessOperations,
      applyToRecurringEvent,
    }: {
      modifyAccessOperations: ApiEntityAccessPatchBody[];
      revokeAccessOperations: EventGroupACLDeleteRequest[];
      applyToRecurringEvent: boolean;
    }) => {
      const modifyAccessPromise = isEmpty(modifyAccessOperations)
        ? Promise.resolve()
        : apiClient.eventGroups.patchEventGroupACL(id, {
            entityAccess: modifyAccessOperations,
            applyToRecurringEvent,
          });

      const revokeAccessPromise = isEmpty(revokeAccessOperations)
        ? Promise.resolve()
        : Promise.allSettled(
            revokeAccessOperations.map((operation) =>
              apiClient.eventGroups.deleteEventGroupACL(id, {
                ...operation,
                applyToRecurringEvent,
              })
            )
          );

      let hasModifyError = false;
      try {
        await modifyAccessPromise;
      } catch {
        hasModifyError = true;
      }

      const revokeResult = await revokeAccessPromise;
      const hasRevokeError =
        revokeResult && some({ status: "rejected" }, revokeResult);

      return {
        success: !hasRevokeError && !hasModifyError,
        revokeError: hasRevokeError,
        modifyError: hasModifyError,
      };
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });

  return {
    accessList,
    isError,
    isLoading,

    grantAccess: grantAccessMutation,
    modifyAccess: modifyAccessMutation,
    revokeAccess: revokeAccessMutation,
    bulkModifyAccess: bulkAccessMutation,
  };
};

export default useUpcomingEventAccess;
